import React, { createContext, Reducer, useReducer } from 'react';
import { Translations } from '../../translations/types';
import defaultTranslations from '../../translations/english.json';
import { getSessionItem, storeSessionItem } from '../../storage/SessionStorage';

export type LocalizationState = {
	translationsInitialized: boolean;
	translations: Translations;
	language: string;
}

export type Actions = UseDefaultTranslations | SetTranslations | SetLanguage;

export type UseDefaultTranslations = {
	type: 'useDefaultTranslations';
}

export type SetTranslations = {
	type: 'setTranslations';
	translations: Translations;
}

export type SetLanguage = {
	type: 'setLanguage';
	language: string;
}

const key = 'language';
const language = new URLSearchParams(window.location.search).get('language') || getSessionItem(key) || '';
storeSessionItem(key, language);

const Dispatch = createContext<React.Dispatch<Actions>>(() => {});
const State = createContext<LocalizationState>({
	translationsInitialized: false,
	translations: defaultTranslations,
	language: language.toLowerCase() || ''
});

const reducer: Reducer<LocalizationState, Actions> = (state, action) => {
	switch (action.type) {
		case 'useDefaultTranslations':
			return {
				...state,
				translationsInitialized: true
			};

		case 'setTranslations':
			return {
				...state,
				translationsInitialized: true,
				translations: action.translations
			};

		case 'setLanguage':
			return {
				...state,
				language: action.language
			};

		default:
			throw new Error('Invalid action type in Localization reducer');
	}
}

function Provider({ children }: { children: JSX.Element[] | JSX.Element }) {
	const [state, dispatch] = useReducer(reducer, {
		translationsInitialized: false,
		translations: defaultTranslations,
		language: language.toLowerCase() || ''
	});

	// The state and the dispatch function should be provided separately so that components that are interested
	// only in dispatching events don't need to use the state and thus re-render when the state changes
	// See here - https://reactjs.org/docs/hooks-faq.html#how-to-avoid-passing-callbacks-down
	return (
		<State.Provider value={state}>
			<Dispatch.Provider value={dispatch}>{children}</Dispatch.Provider>
		</State.Provider>
	)
}

export const Localization = {
	State,
	Dispatch,
	Provider
};